import React, { useEffect, useMemo, useState, useRef, useCallback } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import { RVP_CONTENT, RVP_LIST } from '../data/rendezvousPedagogiques';
import { SLIDE_REGISTRY } from '../components/RvpSlides';
import { useRvpCastReceiver } from '../lib/rvpCast';
import SunModeToggle from '../components/SunModeToggle';
import { Maximize2, WifiOff, Radio, ShieldCheck, CheckCircle2, BookOpen, Volume2, VolumeX } from 'lucide-react';
import { playRvpSound, isSoundMuted, setSoundMuted, unlockAudio } from '../lib/rvpSounds';
import PhoneQcmVote from '../components/PhoneQcmVote';

/**
 * Vue publique CROSS-DEVICE — l'écran distant (vidéoprojecteur, TV, tablette, PC HDMI)
 * reçoit en polling l'état contrôlé par le téléphone de l'enseignant.
 * Route : /present/live/:code
 * Query param : ?viewer=1 = mode élève (masque le helper fullscreen, plus minimaliste)
 */
export default function RvpCastPublicView() {
  const { code } = useParams();
  const [searchParams] = useSearchParams();
  const viewerMode = searchParams.get('viewer') === '1';
  const { state, error, connected } = useRvpCastReceiver(code, { interval: 800 });
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [showHelper, setShowHelper] = useState(!viewerMode);
  const [laserPos, setLaserPos] = useState(null); // { x, y, ts }
  const laserHideTimer = useRef(null);
  const lastCmdTsRef = useRef(0);

  const slug = state?.slug;
  const rvp = useMemo(() => RVP_LIST.find((r) => r.slug === slug), [slug]);
  const rawSlides = useMemo(() => (slug && RVP_CONTENT[slug]) || [], [slug]);
  const participants = state?.participants || { eleves: [] };

  const slides = useMemo(() => {
    if (!participants.eleves || participants.eleves.length === 0) return rawSlides;
    const bienvenue = {
      type: 'bienvenue-perso',
      title: rvp?.title ? `Bienvenue · ${rvp.title}` : 'Bienvenue',
      subtitle: "Prêts pour un moment d'échange et de partage ensemble ?",
      eleves: participants.eleves,
    };
    const merci = {
      type: 'merci-perso',
      subtitle: 'À très vite pour la suite de la formation. Roulez prudemment !',
      next_action: 'À la prochaine séance',
      eleves: participants.eleves,
    };
    return [bienvenue, ...rawSlides, merci];
  }, [participants.eleves, rawSlides, rvp]);

  const current = state?.current_slide ?? 0;
  const revealMap = state?.reveal_map || {};

  const goFullscreen = useCallback(async () => {
    try {
      if (!document.fullscreenElement) await document.documentElement.requestFullscreen?.();
      setShowHelper(false);
    } catch { /* noop */ }
  }, []);

  // Fullscreen change listener
  useEffect(() => {
    const onFs = () => {
      const fs = !!document.fullscreenElement;
      setIsFullscreen(fs);
      if (fs) setShowHelper(false);
    };
    document.addEventListener('fullscreenchange', onFs);
    return () => document.removeEventListener('fullscreenchange', onFs);
  }, []);

  // Commandes distantes (goFullscreen, exitFullscreen)
  useEffect(() => {
    if (!state?.cmd || !state?.cmd_ts) return;
    if (state.cmd_ts === lastCmdTsRef.current) return;
    lastCmdTsRef.current = state.cmd_ts;
    if (state.cmd === 'goFullscreen') goFullscreen();
    if (state.cmd === 'exitFullscreen') {
      try { if (document.fullscreenElement) document.exitFullscreen?.(); } catch { /* noop */ }
    }
  }, [state?.cmd, state?.cmd_ts, goFullscreen]);

  // Pointeur laser : afficher/animer selon les updates du contrôleur
  useEffect(() => {
    if (!state?.laser) return;
    const { x, y, visible, ts } = state.laser;
    if (!visible) {
      setLaserPos(null);
      return;
    }
    setLaserPos({ x, y, ts });
    // Auto-hide après 2s d'inactivité laser
    clearTimeout(laserHideTimer.current);
    laserHideTimer.current = setTimeout(() => setLaserPos(null), 2000);
  }, [state?.laser]);

  // Raccourci F fullscreen
  useEffect(() => {
    const onKey = (e) => {
      if (e.key === 'f' || e.key === 'F') { e.preventDefault(); goFullscreen(); }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [goFullscreen]);

  // ── Sons + flash à chaque changement de slide (projecteur uniquement) ──
  const [muted, setMutedUi] = useState(isSoundMuted());
  const [flash, setFlash] = useState(null); // { ts, strong }
  const prevCurrentRef = useRef(null);
  const prevRevealRef = useRef(false);
  const castCurrent = state?.current_slide ?? 0;

  useEffect(() => {
    const unlock = () => unlockAudio();
    window.addEventListener('pointerdown', unlock, { once: true });
    return () => window.removeEventListener('pointerdown', unlock);
  }, []);

  useEffect(() => {
    if (viewerMode || !state) return;
    if (prevCurrentRef.current === null) { prevCurrentRef.current = castCurrent; return; }
    if (castCurrent === prevCurrentRef.current) return;
    prevCurrentRef.current = castCurrent;
    const t = slides[castCurrent]?.type;
    const sound = (t === 'stat' || t === 'message-sr') ? 'impact'
      : t === 'qcm' ? 'question'
      : (t === 'engagement' || t === 'cta' || t === 'merci-perso') ? 'tada'
      : 'whoosh';
    playRvpSound(sound);
    setFlash({ ts: Date.now(), strong: t === 'stat' || t === 'message-sr' });
  }, [castCurrent, viewerMode, state, slides]);

  useEffect(() => {
    if (viewerMode) return;
    const revealed = !!(state?.reveal_map || {})[castCurrent];
    if (revealed && !prevRevealRef.current) playRvpSound('success');
    prevRevealRef.current = revealed;
  }, [state?.reveal_map, castCurrent, viewerMode]);

  const toggleMute = useCallback(() => {
    const next = !isSoundMuted();
    setSoundMuted(next);
    setMutedUi(next);
    if (!next) { unlockAudio(); playRvpSound('pop'); }
  }, []);

  const formationType = slug === 'am-synthese' ? 'AM' : (slug?.startsWith('aac-') ? 'AAC' : null);

  // Loading / error states
  if (error && state && viewerMode && /introuvable|expir|ferm/i.test(error)) {
    return (
      <div className="fixed inset-0 bg-[#0a0a0b] text-white flex items-center justify-center p-6" data-testid="viewer-session-ended">
        <div className="max-w-sm text-center">
          <CheckCircle2 size={44} className="mx-auto text-emerald-400 mb-4" />
          <h2 className="font-serif-editorial text-white text-[28px] mb-2">Séance terminée — merci !</h2>
          <p className="text-white/60 text-[14px] leading-relaxed">Tu peux maintenant revoir tout le contenu de la séance à ton rythme, directement sur ton téléphone.</p>
          {slug && (
            <a href={`/seance/${slug}`} data-testid="viewer-recap-link"
              className="mt-6 inline-flex items-center justify-center gap-2 h-12 px-6 rounded-full bg-emerald-600 hover:bg-emerald-500 text-white text-[14px] font-bold transition">
              <BookOpen size={15} /> Revoir le rappel du cours
            </a>
          )}
        </div>
      </div>
    );
  }
  if (error && !state) {
    return (
      <div className="fixed inset-0 bg-[#0a0a0b] text-white flex items-center justify-center p-6">
        <div className="max-w-md text-center">
          <WifiOff size={40} className="mx-auto text-red-400 mb-4" />
          <h2 className="font-serif-editorial text-white text-[26px] mb-2">Session introuvable</h2>
          <p className="text-white/60 text-[14px]">{error}</p>
          <p className="text-white/40 text-[12px] mt-4">Code : <span className="font-mono-spec">{code}</span></p>
        </div>
      </div>
    );
  }
  if (!state) {
    return (
      <div className="fixed inset-0 bg-[#0a0a0b] text-white flex items-center justify-center">
        <div className="text-center">
          <div className="inline-flex items-center gap-2 text-white/50 text-[13px] font-mono-spec uppercase tracking-widest">
            <Radio size={14} className="animate-pulse" /> Connexion à la session {code}…
          </div>
        </div>
      </div>
    );
  }
  if (!rvp || rawSlides.length === 0) {
    return <div className="fixed inset-0 bg-black text-white flex items-center justify-center">RVP introuvable ({slug})</div>;
  }

  const slide = slides[current];
  const Component = slide ? SLIDE_REGISTRY[slide.type] : null;
  const currentRevealed = !!revealMap[current];
  const isOfficialSlide = slide && slide.type !== 'bienvenue-perso' && slide.type !== 'merci-perso';
  const textZoom = Math.min(2, Math.max(1, Number(state?.text_zoom) || 1));
  const joinUrl = viewerMode ? null : `${window.location.origin}/rvp/rejoindre/${code}`;
  const pollSessionId = state?.poll_session_id || null;
  const phoneVoteActive = viewerMode && pollSessionId && slide?.type === 'qcm';
  const pollContext = !viewerMode && pollSessionId ? { sessionId: pollSessionId, slug, qIndex: current } : null;

  return (
    <div className="fixed inset-0 bg-[#0a0a0b] overflow-hidden" data-testid="rvp-cast-public">
      {/* Watermark */}
      <div className="absolute top-3 right-4 text-white/20 text-[10px] font-mono-spec uppercase tracking-widest z-10">
        codevoiture.fr · {rvp.code} · live {code} {connected ? <span className="text-emerald-400/60 ml-1">●</span> : <span className="text-red-400/60 ml-1">●</span>}
      </div>

      <div className="h-screen w-screen overflow-hidden">
        <div
          data-testid="rvp-cast-zoom-wrapper"
          style={{ width: `${100 / textZoom}%`, height: `${100 / textZoom}%`, transform: `scale(${textZoom})`, transformOrigin: 'top left' }}
        >
          {phoneVoteActive ? (
            <PhoneQcmVote slide={slide} key={`vote-${current}`} pollSessionId={pollSessionId} slug={slug} qIndex={current} revealed={currentRevealed} />
          ) : Component ? (
            <Component slide={slide} key={current} pollContext={pollContext} forceReveal={currentRevealed} joinUrl={joinUrl} />
          ) : (
            <div className="h-full flex items-center justify-center text-white/50">Type inconnu</div>
          )}
        </div>
      </div>

      {/* Flash d'écran à chaque transition */}
      {flash && (
        <div key={flash.ts} className={`rvp-screen-flash absolute inset-0 z-30 ${flash.strong ? 'bg-red-500/40' : 'bg-white/20'}`} data-testid="rvp-slide-flash" />
      )}

      {!viewerMode && <SunModeToggle />}

      {/* Toggle sons projecteur */}
      {!viewerMode && (
        <button
          onClick={toggleMute}
          data-testid="rvp-sound-toggle"
          title={muted ? 'Activer les sons' : 'Couper les sons'}
          className={`fixed bottom-3 left-3 z-40 w-10 h-10 rounded-full border backdrop-blur-md inline-flex items-center justify-center transition ${muted ? 'border-white/15 bg-black/60 text-white/50' : 'border-emerald-500/40 bg-emerald-950/50 text-emerald-300'}`}
        >
          {muted ? <VolumeX size={15} /> : <Volume2 size={15} />}
        </button>
      )}

      {/* Badge formation officielle */}
      {formationType && isOfficialSlide && (
        <div className="absolute bottom-4 right-4 pointer-events-none z-10">
          <div className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full backdrop-blur-md border shadow-lg ${
            formationType === 'AAC' ? 'border-red-500/50 bg-red-950/60 text-red-200' : 'border-sky-500/50 bg-sky-950/60 text-sky-200'
          }`}>
            <ShieldCheck size={11} />
            <span className="text-[10px] font-bold font-mono-spec uppercase tracking-[0.2em]">{formationType} · Officiel</span>
          </div>
        </div>
      )}

      {/* Pointeur laser virtuel */}
      {laserPos && (
        <div
          className="pointer-events-none absolute z-40"
          style={{
            left: `${laserPos.x * 100}%`,
            top: `${laserPos.y * 100}%`,
            transform: 'translate(-50%, -50%)',
          }}
          data-testid="laser-pointer"
        >
          <div className="relative">
            <div className="absolute inset-0 rounded-full bg-red-500/40 blur-2xl scale-150 animate-pulse" style={{ width: 60, height: 60, marginLeft: -30, marginTop: -30 }} />
            <div className="rounded-full bg-red-500 shadow-[0_0_40px_10px_rgba(239,68,68,0.6)] border-2 border-white/80" style={{ width: 24, height: 24 }} />
          </div>
        </div>
      )}

      {/* Bouton flottant Plein écran */}
      {!isFullscreen && (
        <button
          onClick={goFullscreen}
          data-testid="rvp-cast-fullscreen"
          className="fixed top-3 left-3 z-20 inline-flex items-center gap-1.5 h-9 px-3 rounded-full border border-white/15 bg-black/60 backdrop-blur-md text-white/80 hover:text-white text-[11.5px] font-semibold transition"
        >
          <Maximize2 size={12} /> Plein écran (F)
        </button>
      )}

      {/* Helper au démarrage */}
      {showHelper && !isFullscreen && (
        <div className="fixed inset-0 z-30 bg-black/85 backdrop-blur-sm flex items-center justify-center p-6" data-testid="rvp-cast-helper">
          <div className="max-w-lg w-full rounded-2xl border border-red-500/40 bg-[#0d0d0e] p-6 sm:p-8 text-center">
            <div className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full border border-emerald-500/50 bg-emerald-500/10 text-emerald-300 text-[10.5px] font-mono-spec uppercase tracking-wider font-bold">
              <Radio size={11} className="animate-pulse" /> Connecté · Session {code}
            </div>
            <h2 className="mt-4 font-serif-editorial text-white text-[26px] sm:text-[32px] leading-tight">
              Le téléphone contrôle cet écran
            </h2>
            <p className="text-white/60 text-[13.5px] mt-3 leading-relaxed">
              Passe cet écran en <strong className="text-white">plein écran</strong> pour la salle. Ton téléphone reste ta télécommande — les slides défileront automatiquement.
            </p>
            <button
              onClick={goFullscreen}
              data-testid="rvp-cast-helper-fullscreen"
              className="mt-6 w-full h-14 rounded-full bg-red-600 hover:bg-red-500 text-white text-[15px] font-bold inline-flex items-center justify-center gap-2 transition shadow-lg shadow-red-900/40"
            >
              <Maximize2 size={16} /> Passer en plein écran
            </button>
            <button
              onClick={() => setShowHelper(false)}
              className="mt-2 text-white/40 hover:text-white/70 text-[12px] underline"
            >
              Masquer cette aide
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
